import { useEffect, useRef } from "react";
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  Link,
  Modal,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Close, tpTheme } from "@trainingpeaks/designtokens";

const baseModalStyle = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
};

export const SURVEY_REASONS = [
  {
    value: "Price / Financial Reasons",
    subtitle: "The subscription cost no longer fits my budget.",
  },
  {
    value: "Injured / Health Reasons",
    subtitle: "I'm unable to train due to injury or health.",
  },
  {
    value: "Finished Goal Event",
    subtitle: "I completed my target race or event.",
  },
  {
    value: "Poor Experience / Fit",
    subtitle: "The coaching style or program wasn't the right match.",
  },
  {
    value: "Temporary Pause / Restart Later",
    subtitle: "Life got busy — I'd like to come back when I'm ready.",
  },
] as const;

export const OTHER_VALUE = "Other";

const SUPPORT_EMAIL = "coachmatch@trainingpeaks.com";

const SURVEY_TITLE_ID = "coach-match-survey-modal-title";
const SURVEY_SUBTITLE_ID = "coach-match-survey-modal-subtitle";
const CONFIRM_TITLE_ID = "coach-match-cancel-modal-title";
const CONFIRM_DESCRIPTION_ID = "coach-match-cancel-modal-description";

export type CancelFlowStep = "survey" | "confirm";

interface CoachMatchCancelFlowModalProps {
  isOpen: boolean;
  step: CancelFlowStep;
  selectedReason: string;
  additionalText: string;
  isOther: boolean;
  isContinueDisabled: boolean;
  isProcessing: boolean;
  onReasonChange: (reason: string) => void;
  onAdditionalTextChange: (text: string) => void;
  onContinue: () => void;
  onKeepSubscription: () => void;
  onConfirm: () => void;
  onClose: () => void;
}

const CoachMatchCancelFlowModal = ({
  isOpen,
  step,
  selectedReason,
  additionalText,
  isOther,
  isContinueDisabled,
  isProcessing,
  onReasonChange,
  onAdditionalTextChange,
  onContinue,
  onKeepSubscription,
  onConfirm,
  onClose,
}: CoachMatchCancelFlowModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"));
  const modalStyle = { ...baseModalStyle, width: isNarrow ? "95%" : "40%" };
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOther && scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }
  }, [isOther]);

  if (step === "confirm") {
    const mailtoSubject = t("Coach Match subscription: Pause, Adjust, or Speak with Specialist");
    const mailtoBody = t(
      // eslint-disable-next-line max-len
      "Hi Coach Match Team,\n\nI am reaching out regarding my coaching membership. Before deciding whether to cancel, I would like to [pause my membership / adjust my package level / speak with a Coach Match Specialist].\n\nMy account email is: [Your Email]\n\nThanks!",
    );
    // eslint-disable-next-line max-len
    const mailtoHref = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(mailtoSubject)}&body=${encodeURIComponent(mailtoBody)}`;

    return (
      <Modal open={isOpen}>
        <Box
          role="dialog"
          aria-modal="true"
          aria-labelledby={CONFIRM_TITLE_ID}
          aria-describedby={CONFIRM_DESCRIPTION_ID}
          sx={modalStyle}
          data-testid="coach-match-cancel-modal"
          className="max-w-xl px-8 pb-8 pt-2"
        >
          <div className="flex justify-end">
            <IconButton
              onClick={onClose}
              aria-label={t("Close")}
              className="px-0"
              disabled={isProcessing}
            >
              <Close size="24" fill={tpTheme.semanticLayer.colors.disabled.main} />
            </IconButton>
          </div>
          <Typography id={CONFIRM_TITLE_ID} variant="h1" className="font-bold mb-4 text-xl md:text-2xl">
            {t("Are you sure you want to cancel coaching?")}
          </Typography>
          <Typography id={CONFIRM_DESCRIPTION_ID} variant="body1" className="mb-4">
            {/* eslint-disable-next-line max-len */}
            {t("Reaching your goals takes the right fit. Before you cancel, let us know if you need to pause, adjust your package level, or speak directly with a Coach Match Specialist.")}
          </Typography>
          <Link
            href={mailtoHref}
            underline="none"
            className="mb-6 inline-block font-semibold"
          >
            {"📧 "}
            {t("Pause, Adjust, or Contact Support")}
            {` (${SUPPORT_EMAIL})`}
          </Link>
          <div className="flex flex-row gap-3 justify-end">
            <Button
              variant="text"
              color="primary"
              onClick={onConfirm}
              disabled={isProcessing}
              className="w-full sm:w-auto"
            >
              {t("Yes, Cancel")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={onClose}
              disabled={isProcessing}
              className="w-full sm:w-auto"
            >
              {t("Keep My Coaching")}
            </Button>
          </div>
        </Box>
      </Modal>
    );
  }

  return (
    <Modal open={isOpen}>
      <Box
        role="dialog"
        aria-modal="true"
        aria-labelledby={SURVEY_TITLE_ID}
        aria-describedby={SURVEY_SUBTITLE_ID}
        sx={modalStyle}
        data-testid="coach-match-survey-modal"
        className="max-w-xl px-4 sm:px-8 pb-6 sm:pb-8 pt-4 max-h-[90vh] flex flex-col"
      >
        <div className="flex justify-between items-start">
          <Typography id={SURVEY_TITLE_ID} variant="h1" className="font-bold text-xl md:text-2xl" sx={{ mb: 0 }}>
            {t("Before you go...")}
          </Typography>
          <IconButton
            onClick={onKeepSubscription}
            aria-label={t("Close")}
            className="px-0"
          >
            <Close size="24" fill={tpTheme.semanticLayer.colors.disabled.main} />
          </IconButton>
        </div>
        <Typography id={SURVEY_SUBTITLE_ID} variant="body1" className="mb-2 sm:mb-4">
          {t("What's the reason for canceling?")}
        </Typography>
        <div ref={scrollRef} className="overflow-y-auto min-h-0">
          <RadioGroup
            aria-labelledby={SURVEY_SUBTITLE_ID}
            value={selectedReason}
            onChange={(e) => onReasonChange(e.target.value)}
            data-testid="survey-radio-group"
          >
            {SURVEY_REASONS.map((reason) => (
              <Box
                key={reason.value}
                className="mb-2 rounded-lg border border-solid border-divider-light p-3"
              >
                <FormControlLabel
                  value={reason.value}
                  control={<Radio />}
                  label={
                    <div>
                      <Typography variant="body1" className="font-semibold">
                        {t(reason.value)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t(reason.subtitle)}
                      </Typography>
                    </div>
                  }
                  className="m-0 w-full items-start"
                />
              </Box>
            ))}
            <Box
              className="mb-2 rounded-lg border border-solid border-divider-light p-3"
            >
              <FormControlLabel
                value={OTHER_VALUE}
                control={<Radio />}
                label={
                  <Typography variant="body1" className="font-semibold">
                    {t("Other")}
                  </Typography>
                }
                className="m-0 w-full"
              />
              {isOther && (
                <TextField
                  placeholder={t("Optional: Why?")}
                  value={additionalText}
                  onChange={(e) => onAdditionalTextChange(e.target.value)}
                  fullWidth
                  size="small"
                  className="mt-2"
                  onFocus={(e) => {
                    setTimeout(() => {
                      e.target.scrollIntoView({ behavior: "smooth", block: "center" });
                    }, 300);
                  }}
                  inputProps={{ "data-testid": "other-text-input" }}
                />
              )}
            </Box>
          </RadioGroup>
        </div>
        <div className="flex flex-row gap-3 justify-end mt-4 sm:mt-6 shrink-0">
          <Button
            variant="outlined"
            color="primary"
            onClick={onKeepSubscription}
            className="w-full sm:w-auto"
          >
            {t("Keep Subscription")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onContinue}
            disabled={isContinueDisabled}
            className="w-full sm:w-auto"
          >
            {t("Continue")}
          </Button>
        </div>
      </Box>
    </Modal>
  );
};

export default CoachMatchCancelFlowModal;
